import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAorText } from 'utils/aorTerminology';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { NULL_VALUE } from 'constants/displayConstants';
import type { GroupRosterItem } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  AssetFormLayoutToggle,
  type AssetFormLayout,
} from './DhsResourceAddDialog';
import {
  DHS_ASSET_EDIT_FORM_ID,
  DhsResourceEditForm,
} from './DhsResourceEditForm';
import { formatCheckInStatus, isAssignedToIncident } from './checkInStatus';
import type { ResourceRow } from './ResourceTabCard';
import {
  displayOrNull,
  formatAorName,
  formatAssignees,
  formatCoord,
  formatCostAmount,
  formatCostPerTime,
  formatIncidentName,
  formatPersonsOnBoard,
  formatRequestStatus,
  formatResourceName,
  formatSymbology,
  formatUser,
} from './dhsResourceDisplay';
import {
  threatChecklistCoverage,
  threatChecklistItemsForCategory,
} from '../EventsTab/categoryChecklistMatch';
import { AssetMediaSection } from './AssetMediaSection';
import { AssetImportProvenance } from './AssetImportProvenance';
import { ResourceThreatChecklistBadge } from './ResourceThreatChecklistBadges';
import { ThreatChecklistCoverageRing } from './ThreatChecklistCoverageRing';
import { hasLinkedIcs213rr } from 'components/IAP/ResourceRequests/ics213rr/linkedIcs213rr';

function formatLinkedIcs213rr(value: ResourceRow['linked_ics213rr']): string {
  if (!hasLinkedIcs213rr(value)) return NULL_VALUE;
  const lineCount = value.items?.length ?? 0;
  const lines = `${lineCount} line item${lineCount === 1 ? '' : 's'}`;
  return value.name?.trim() ? `${value.name.trim()} (${lines})` : lines;
}

function checklistKindLabel(kind: string): string {
  if (kind === 'org_action') return 'Action';
  if (kind === 'send_notification') return 'Notification';
  return kind;
}

function DetailField({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit: () => void;
}) {
  return (
    <button
      type="button"
      className="tw:min-w-0 tw:rounded-md tw:border tw:border-transparent tw:bg-transparent tw:p-1 tw:text-left tw:hover:bg-muted"
      onClick={onEdit}
    >
      <div className="tw:text-xs tw:font-medium tw:text-foreground">
        {label}
      </div>
      <div className="tw:text-xs tw:break-words tw:text-muted-foreground">
        {value}
      </div>
    </button>
  );
}

export function DhsResourceDetailsDialog({
  row,
  open,
  onOpenChange,
  mode,
  onModeChange,
  openedInEdit,
  groupMembers,
  onRowPatch,
  onDelete,
}: {
  row: ResourceRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'view' | 'edit';
  onModeChange: (mode: 'view' | 'edit') => void;
  openedInEdit: boolean;
  groupMembers: GroupRosterItem[];
  onRowPatch: (patch: Partial<ResourceRow>) => void;
  /** When set, the footer offers Delete. */
  onDelete?: (row: ResourceRow) => void;
}) {
  const aorText = useAorText();
  const hwcg = useHwcgFlag();
  const [showAllDetails, setShowAllDetails] = useState(false);
  const { data: aors = [] } = useAors();
  const [layout, setLayout] = useState<AssetFormLayout>('vertical');

  const enterEdit = () => onModeChange('edit');

  const finishEdit = (patch?: Partial<ResourceRow>) => {
    if (patch) onRowPatch(patch);
    if (openedInEdit) {
      onOpenChange(false);
      return;
    }
    onModeChange('view');
  };

  const fields: { label: string; value: string }[] = row
    ? [
        { label: 'ID', value: displayOrNull(row.resource_set_id) },
        { label: 'Name', value: formatResourceName(row) },
        { label: 'Kind', value: displayOrNull(row.kind) },
        { label: 'Type', value: displayOrNull(row.type) },
        { label: 'Asset Status', value: displayOrNull(row.assetStatus) },
        {
          label: 'Asset Status Timestamp',
          value: row.assetStatusTimestamp
            ? formatDateTime(row.assetStatusTimestamp)
            : NULL_VALUE,
        },
        {
          label: 'Check-in Status',
          value: formatCheckInStatus(row.status, isAssignedToIncident(row)),
        },
        { label: 'Quantity', value: displayOrNull(row.quantity) },
        { label: 'Cost', value: formatCostAmount(row.cost) },
        {
          label: 'Cost per time',
          value: formatCostPerTime(row.cost_per_time, row.cost_time_unit),
        },
        { label: 'Unit', value: displayOrNull(row.unit) },
        {
          label: 'Hull / Tail Number',
          value: displayOrNull(row.hull_tail_number),
        },
        {
          label: 'Persons on Board',
          value: formatPersonsOnBoard(row.persons_on_board),
        },
        { label: aorText('AOR'), value: formatAorName(aors, row.aor) },
        { label: 'OPCON', value: formatAorName(aors, row.opcon) },
        { label: 'TACON', value: formatAorName(aors, row.tacon) },
        { label: 'Incident', value: formatIncidentName(row) },
        { label: 'Request Status', value: formatRequestStatus(row) },
        {
          label: 'Owning Organization',
          value: displayOrNull(row.owning_organization),
        },
        { label: 'Address', value: displayOrNull(row.address) },
        { label: 'Point of Contact', value: formatUser(row.pointOfContact) },
        { label: 'Assignees', value: formatAssignees(row.assignees) },
        { label: 'Description', value: displayOrNull(row.description) },
        { label: 'Capabilities', value: displayOrNull(row.capabilities) },
        { label: 'Symbology', value: formatSymbology(row.symbology) },
        { label: 'Latitude', value: formatCoord(row.latitude) },
        { label: 'Longitude', value: formatCoord(row.longitude) },
        {
          label: 'Datetime Ordered',
          value: row.datetimeOrdered
            ? formatDateTime(row.datetimeOrdered)
            : NULL_VALUE,
        },
        {
          label: 'Datetime Ordered Offset',
          value: displayOrNull(row.datetimeOrderedOffset),
        },
        { label: 'ETA', value: row.eta ? formatDateTime(row.eta) : NULL_VALUE },
        { label: 'ETA Offset', value: displayOrNull(row.etaTimezoneOffset) },
        { label: 'Notes', value: displayOrNull(row.notes) },
        {
          label: 'Linked ICS 213RR',
          value: formatLinkedIcs213rr(row.linked_ics213rr),
        },
        {
          label: 'Template',
          value:
            row.isTemplate == null ? NULL_VALUE : row.isTemplate ? 'Yes' : 'No',
        },
        { label: 'Row ID', value: displayOrNull(row.id) },
      ]
    : [];

  const fieldByLabel = (label: string) =>
    fields.find((field) => field.label === label);
  const currentLocation = row
    ? [
        row.address,
        row.latitude != null && row.longitude != null
          ? `${formatCoord(row.latitude)}, ${formatCoord(row.longitude)}`
          : null,
      ]
        .filter((part) => part && part !== NULL_VALUE)
        .join(' — ') || NULL_VALUE
    : NULL_VALUE;
  const HWCG_SUMMARY_LABELS = ['Asset Status', 'Incident', 'Request Status'];
  // Shown above or folded into the summary, so not repeated under "all details".
  const HWCG_SUMMARY_HIDDEN_LABELS = [
    'Name',
    'Description',
    'Capabilities',
    'Address',
    'Latitude',
    'Longitude',
    ...HWCG_SUMMARY_LABELS,
  ];
  const hwcgSummaryFields = HWCG_SUMMARY_LABELS.flatMap((label) => {
    const field = fieldByLabel(label);
    return field ? [field] : [];
  });
  const hwcgExtraFields = fields.filter(
    (field) => !HWCG_SUMMARY_HIDDEN_LABELS.includes(field.label)
  );

  const isEditing = mode === 'edit' && !!row;
  const coverage = row
    ? threatChecklistCoverage({
        threats: row.threats,
        threat_checklists: row.threat_checklists,
      })
    : null;

  return (
    <Dialog open={open && !!row} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'dark tw:flex tw:max-h-[85vh] tw:flex-col tw:overflow-hidden tw:bg-background tw:text-foreground',
          isEditing
            ? layout === 'horizontal'
              ? 'tw:!max-w-3xl'
              : 'tw:!max-w-lg'
            : 'sm:tw:max-w-3xl'
        )}
      >
        <DialogHeader
          className={
            isEditing
              ? 'tw:!w-full tw:!flex-row tw:items-center tw:justify-between tw:pr-8'
              : undefined
          }
        >
          <DialogTitle>
            {isEditing
              ? `Edit ${row?.name || 'resource'}`
              : row?.name || 'Resource details'}
          </DialogTitle>
          {isEditing ? (
            <AssetFormLayoutToggle layout={layout} onLayoutChange={setLayout} />
          ) : null}
        </DialogHeader>
        {isEditing && row ? (
          <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
            <DhsResourceEditForm
              row={row}
              groupMembers={groupMembers}
              layout={layout}
              hideInlineActions
              onDone={finishEdit}
            />
          </div>
        ) : (
          <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto">
            <AssetImportProvenance
              resourceSetId={row?.resource_set_id}
              onUndone={() => onOpenChange(false)}
            />
            {hwcg ? (
              <>
                {row ? (
                  <AssetMediaSection
                    section="image"
                    imageUrl={row.image_url}
                    documentFolders={row.document_folders}
                    onEdit={enterEdit}
                  />
                ) : null}
                <div className="tw:grid tw:grid-cols-1 tw:gap-3">
                  <DetailField
                    label="Description"
                    value={displayOrNull(row?.description)}
                    onEdit={enterEdit}
                  />
                  <DetailField
                    label="Capabilities"
                    value={displayOrNull(row?.capabilities)}
                    onEdit={enterEdit}
                  />
                </div>
                {row ? (
                  <AssetMediaSection
                    section="folders"
                    resourceSetId={row.resource_set_id}
                    assetName={row.name}
                    imageUrl={row.image_url}
                    documentFolders={row.document_folders}
                    onEdit={enterEdit}
                    onFoldersChange={(next) =>
                      onRowPatch({ document_folders: next })
                    }
                  />
                ) : null}
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 sm:tw:grid-cols-2">
                  {hwcgSummaryFields.map((field) => (
                    <DetailField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      onEdit={enterEdit}
                    />
                  ))}
                  <DetailField
                    label="Current Location"
                    value={currentLocation}
                    onEdit={enterEdit}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tw:self-start"
                  aria-expanded={showAllDetails}
                  onClick={() => setShowAllDetails((prev) => !prev)}
                >
                  {showAllDetails ? 'Hide All Details' : 'Show All Details'}
                </Button>
                {showAllDetails ? (
                  <div className="tw:grid tw:grid-cols-1 tw:gap-3 sm:tw:grid-cols-2">
                    {hwcgExtraFields.map((field) => (
                      <DetailField
                        key={field.label}
                        label={field.label}
                        value={field.value}
                        onEdit={enterEdit}
                      />
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                {row ? (
                  <AssetMediaSection
                    imageUrl={row.image_url}
                    documentFolders={row.document_folders}
                    onEdit={enterEdit}
                  />
                ) : null}
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 sm:tw:grid-cols-2">
                  {fields.map((field) => (
                    <DetailField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      onEdit={enterEdit}
                    />
                  ))}
                </div>
              </>
            )}
            {!hwcg || showAllDetails ? (
              <button
                type="button"
                className="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:border-transparent tw:bg-transparent tw:p-1 tw:text-left tw:hover:bg-muted"
                onClick={enterEdit}
              >
                {row && coverage ? (
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <ThreatChecklistCoverageRing
                      threats={row.threats}
                      threat_checklists={row.threat_checklists}
                      size={22}
                    />
                    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      {coverage.total === 0
                        ? 'No automation rules'
                        : `${coverage.covered} of ${coverage.total} automation rules have a response checklist (${coverage.percent}%)`}
                    </p>
                  </div>
                ) : null}
                {!row?.threats?.length ? (
                  <p className="tw:text-xs tw:text-muted-foreground">
                    No automation rules selected. Add rules to attach a response
                    checklist.
                  </p>
                ) : (
                  row.threats.map((threat) => {
                    const items = threatChecklistItemsForCategory(
                      row.threat_checklists,
                      row.threats,
                      threat
                    );
                    return (
                      <div key={threat} className="tw:min-w-0 tw:pl-1">
                        <div className="tw:mb-1 tw:flex tw:items-center tw:gap-2">
                          <ResourceThreatChecklistBadge
                            threat={threat}
                            hasChecklist={items.length > 0}
                          />
                        </div>
                        {items.length === 0 ? (
                          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                            No checklist items for this automation rule.
                          </p>
                        ) : (
                          <ul className="tw:m-0 tw:flex tw:flex-col tw:gap-1 tw:p-0">
                            {items.map((item) => (
                              <li
                                key={item.id}
                                className="tw:text-xs tw:text-muted-foreground"
                              >
                                {item.label} ({checklistKindLabel(item.kind)})
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </button>
            ) : null}
          </div>
        )}
        <DialogFooter>
          {onDelete && row?.resource_set_id ? (
            <Button
              type="button"
              variant="destructive"
              className="tw:sm:mr-auto"
              onClick={() => onDelete(row)}
            >
              <Trash2 />
              Delete
            </Button>
          ) : null}
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => finishEdit()}
              >
                Cancel
              </Button>
              <Button type="submit" form={DHS_ASSET_EDIT_FORM_ID}>
                Save
              </Button>
            </>
          ) : (
            <Button type="button" disabled={!row} onClick={enterEdit}>
              <Pencil />
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
